import { useEffect, useRef, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { Calendar, Menu, Phone, X, ChevronDown, ChevronUp } from "lucide-react";
import { Container } from "./Container";
import { ButtonLink } from "./Button";
import logoAsset from "@/assets/durafoam-logo-trim.webp.asset.json";
import { useInspectionDialog } from "./InspectionDialog";


const navLinks = [
  { label: "Learning Center", href: "/blog" },
  { label: "About Us", href: "/about-us" },
  { label: "Contact", href: "/contact-us" },
];

const residentialServices = [
  { label: "Foam Roofing Installation", href: "/foam-roofing" },
  { label: "Foam Roof Repair", href: "/foam-roof-repair-phoenix" },
  { label: "Foam Roof Coating & Recoating", href: "/foam-roof-coating-recoating" },
  { label: "Roof Repair", href: "/roof-repair" },
  { label: "Roof Leak Repair", href: "/roof-leak-repair" },
  { label: "Storm & Hail Damage Repair", href: "/storm-damage-repair" },
  { label: "Hail Damage Roof Inspection", href: "/hail-damage-roof-inspection-phoenix" },
  { label: "Roof Replacement", href: "/roof-replacement-phoenix" },
  { label: "Tile Roofing", href: "/tile-roofing" },
  { label: "Tile Roof Repair", href: "/tile-roof-repair-phoenix" },
  { label: "Shingle Roofing", href: "/shingle-roofing" },
  { label: "Flat Roof Repair", href: "/flat-roof-repair" },
  { label: "Emergency Roof Leak Repair", href: "/emergency-roof-leak-repair" },
  { label: "Skylight Repair", href: "/skylight-repair" },
  { label: "Skylight Replacement", href: "/skylight-replacement-phoenix" },
];

const commercialServices = [
  { label: "Commercial Roofing", href: "/commercial-roofing" },
  { label: "Commercial Foam Roofing", href: "/commercial-foam-roofing" },
  { label: "Commercial Roof Repair", href: "/commercial-roof-repair" },
  { label: "Commercial Roof Coating", href: "/commercial-roof-coating" },
  { label: "Warehouse Roofing", href: "/warehouse-roofing" },
  { label: "HOA Roofing", href: "/hoa-roofing" },
  { label: "Apartment & Condo Roofing", href: "/apartment-roofing" },
];

const maintenanceServices = [
  { label: "Roof Maintenance Plans", href: "/roof-maintenance" },
  { label: "Roof Inspections for Realtors", href: "/roof-inspections-for-realtors" },
  { label: "Free Roof Inspection", href: "/free-roof-inspection-phoenix" },
];

type MenuKey = "services" | "areas";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { openInspection } = useInspectionDialog();
  const [menu, setMenu] = useState<MenuKey | null>(null);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    const unsub = router.subscribe("onResolved", () => setMenu(null));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      unsub();
      window.removeEventListener("keydown", onKey);
    };
  }, [router]);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = (key: MenuKey) => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setMenu((m) => (m === key ? null : m));
    }, 120);
  };
  const openMenu = (key: MenuKey) => {
    cancelClose();
    setMenu(key);
  };
  const closeMenu = () => {
    cancelClose();
    setMenu(null);
  };
  const triggerProps = (key: MenuKey) => ({
    onMouseEnter: () => openMenu(key),
    onFocus: () => openMenu(key),
  });
  const wrapperProps = (key: MenuKey) => ({
    onMouseEnter: () => cancelClose(),
    onMouseLeave: () => scheduleClose(key),
  });
  const panelProps = (key: MenuKey) => ({
    onMouseEnter: () => openMenu(key),
  });

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background text-primary shadow-[0_2px_12px_rgba(27,43,107,0.10)]">
      {/* Utility bar (desktop) */}
      <div className="hidden border-b border-accent/30 bg-primary lg:block">
        <Container className="flex items-stretch justify-between gap-6">
          <p className="flex items-center gap-2 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/90">
            Serving Phoenix &amp; the Valley Since 1989
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent" />
            Licensed ROC223900
          </p>
          <div className="flex items-stretch">
            <a
              href="tel:+14809415373"
              className="flex items-center gap-2 border-x border-white/15 px-5 text-[14px] font-bold uppercase tracking-[0.06em] text-white transition-colors hover:bg-white/10"
            >
              <Phone className="h-4 w-4 text-accent" aria-hidden="true" />
              Call 480-941-5373
            </a>
            <button
              type="button"
              onClick={() => openInspection()}
              className="flex items-center gap-2 bg-accent px-6 text-[14px] font-bold uppercase tracking-[0.06em] text-white transition-colors hover:bg-[#d96d1d]"
            >
              <Calendar className="h-4 w-4" aria-hidden="true" />
              Schedule Now
            </button>
          </div>
        </Container>
      </div>

      <Container className="flex h-[80px] items-center justify-between gap-4 lg:h-[112px]">
        <Link to="/" className="flex min-w-0 items-center gap-2.5" aria-label="Durafoam Roofing — home">
          <img
            src={logoAsset.url}
            alt="Durafoam Roofing"
            width={490}
            height={194}
            className="h-[46px] w-auto shrink-0 lg:h-[86px]"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="ml-auto hidden lg:flex items-center gap-1" aria-label="Primary">

          <div className="relative" {...wrapperProps("services")}>
            <button
              type="button"
               className="flex items-center gap-1 relative rounded-md px-3.5 py-2 text-[13px] font-bold uppercase tracking-[0.08em] text-primary/85 transition-colors hover:text-primary after:absolute after:inset-x-3.5 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:bg-accent after:transition-transform hover:after:scale-x-100"
              aria-haspopup="true"
              aria-expanded={menu === "services"}
              {...triggerProps("services")}
            >
              Services <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </button>
            {menu === "services" && (
              <div {...panelProps("services")} className="absolute left-0 top-full grid w-[50rem] grid-cols-3 gap-x-6 gap-y-1 rounded-lg border border-border bg-white p-5 shadow-lg">
                <div>
                  <p className="px-3 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-primary/70">
                    Residential
                  </p>
                  {residentialServices.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      onClick={closeMenu}
                      className="block rounded-md px-3 py-2 text-sm text-foreground hover:bg-surface hover:text-primary"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
                <div>
                  <p className="px-3 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-primary/70">
                    Commercial & Specialty
                  </p>
                  {commercialServices.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      onClick={closeMenu}
                      className="block rounded-md px-3 py-2 text-sm text-foreground hover:bg-surface hover:text-primary"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
                <div>
                  <p className="px-3 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-primary/70">
                    Maintenance & Protection
                  </p>
                  {maintenanceServices.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      onClick={closeMenu}
                      className="block rounded-md px-3 py-2 text-sm text-foreground hover:bg-surface hover:text-primary"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/service-areas"
            className="relative rounded-md px-3.5 py-2 text-[13px] font-bold uppercase tracking-[0.08em] text-primary/85 transition-colors hover:text-primary after:absolute after:inset-x-3.5 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:bg-accent after:transition-transform hover:after:scale-x-100"
          >
            Service Areas
          </Link>


          {navLinks.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className="relative rounded-md px-3.5 py-2 text-[13px] font-bold uppercase tracking-[0.08em] text-primary/85 transition-colors hover:text-primary after:absolute after:inset-x-3.5 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:bg-accent after:transition-transform hover:after:scale-x-100"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Mobile actions */}
        <div className="flex items-center gap-1.5 lg:hidden">
          <button
            type="button"
            onClick={() => openInspection()}
            className="flex h-11 items-center gap-1.5 rounded-md bg-accent px-3.5 text-[13px] font-bold uppercase tracking-[0.04em] text-primary"
          >
            <Calendar className="h-4 w-4" aria-hidden="true" /> Schedule
          </button>
          <a
            href="tel:+14809415373"
            aria-label="Call Durafoam Roofing at 480-941-5373"
            className="grid h-11 w-11 place-items-center rounded-md text-primary hover:bg-surface"
          >
            <Phone className="h-5 w-5" aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="grid h-11 w-11 place-items-center rounded-md text-primary hover:bg-surface"
          >
            {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </Container>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-primary max-h-[80vh] overflow-y-auto">
          <Container className="flex flex-col py-4">
            {/* Top quick actions */}
            <div className="flex flex-col gap-2 pb-4 border-b border-white/10">
              <ButtonLink
                href="/free-roof-inspection-phoenix"
                variant="primary"
                onClick={(e) => { e.preventDefault(); setOpen(false); openInspection(); }}
                className="gap-1.5 justify-center"
              >
                <Calendar className="h-5 w-5" aria-hidden="true" /> Schedule Now
              </ButtonLink>
              <ButtonLink
                href="/contact-us"
                variant="ghost-light"
                onClick={() => setOpen(false)}
              >
                Contact Us
              </ButtonLink>
              <a
                href="tel:+14809415373"
                className="flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-[15px] font-medium text-white hover:bg-white/10"
                onClick={() => setOpen(false)}
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                Call 480-941-5373
              </a>
            </div>

            {/* Accordion groups */}
            <div className="py-2">
              {/* Services */}
              <div className="border-b border-white/10">
                <button
                  type="button"
                  onClick={() =>
                    setOpenAccordion((prev) => (prev === "services" ? null : "services"))
                  }
                  className="flex w-full items-center justify-between rounded-md px-3 py-3 text-[15px] font-medium text-white hover:bg-white/10"
                  aria-expanded={openAccordion === "services"}
                >
                  Services
                  {openAccordion === "services" ? (
                    <ChevronUp className="h-5 w-5 text-white/70" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-white/70" aria-hidden="true" />
                  )}
                </button>
                {openAccordion === "services" && (
                  <div className="pb-2 px-3">
                    <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/70">
                      Residential
                    </p>
                    {residentialServices.map((s) => (
                      <Link
                        key={s.href}
                        to={s.href}
                        onClick={() => setOpen(false)}
                        className="block rounded-md px-3 py-2 text-sm text-white/85 hover:bg-white/10"
                      >
                        {s.label}
                      </Link>
                    ))}
                    <p className="mt-3 px-3 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/70">
                      Commercial & Specialty
                    </p>
                    {commercialServices.map((s) => (
                      <Link
                        key={s.href}
                        to={s.href}
                        onClick={() => setOpen(false)}
                        className="block rounded-md px-3 py-2 text-sm text-white/85 hover:bg-white/10"
                      >
                        {s.label}
                      </Link>
                    ))}
                    <p className="mt-3 px-3 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/70">
                      Maintenance & Protection
                    </p>
                    {maintenanceServices.map((s) => (
                      <Link
                        key={s.href}
                        to={s.href}
                        onClick={() => setOpen(false)}
                        className="block rounded-md px-3 py-2 text-sm text-white/85 hover:bg-white/10"
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Service Areas */}
              <div className="border-b border-white/10">
                <Link
                  to="/service-areas"
                  onClick={() => setOpen(false)}
                  className="flex w-full items-center justify-between rounded-md px-3 py-3 text-[15px] font-medium text-white hover:bg-white/10"
                >
                  Service Areas
                  <span aria-hidden>→</span>
                </Link>
              </div>


              {/* Company */}
              <div className="border-b border-white/10">
                <button
                  type="button"
                  onClick={() =>
                    setOpenAccordion((prev) => (prev === "company" ? null : "company"))
                  }
                  className="flex w-full items-center justify-between rounded-md px-3 py-3 text-[15px] font-medium text-white hover:bg-white/10"
                  aria-expanded={openAccordion === "company"}
                >
                  Company
                  {openAccordion === "company" ? (
                    <ChevronUp className="h-5 w-5 text-white/70" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-white/70" aria-hidden="true" />
                  )}
                </button>
                {openAccordion === "company" && (
                  <div className="pb-2 px-3">
                    {navLinks.map((l) => (
                      <Link
                        key={l.href}
                        to={l.href}
                        onClick={() => setOpen(false)}
                        className="block rounded-md px-3 py-2 text-sm text-white/85 hover:bg-white/10"
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </Container>
        </div>
      )}
    </header>
  );
}
